import React, { useEffect, useMemo, useState } from "react";
import { ContentContainer } from "@layout";
import {
  Breadcrumbs,
  Button,
  Modal,
  RadioButtonList,
  Textfield,
} from "../../../components/core";
import { Strings } from "../../../constants/strings";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  ALL_OPERATOR_ACCOUNTS,
  UPDATE_USER_ACCESS_PERMISSION,
} from "../../../constants/graphql/auth-queries";
import { Accordion, AccordionTab } from "primereact/accordion";
import "./styles.css";
import { noPermissionRole, pageListArray } from "./lists";
import { useSelector } from "react-redux";
import useSidebarStore from "../../../zustand/stores/sidebar-store";
import { useNavigate, useParams } from "react-router-dom";
import {
  GET_ACCOUNT_ACCESS_PERMISSIONS,
  OPERATOR_ACCOUNT_ROLE_BY_ID,
} from "../../../constants/graphql/operator_account_roles";
import { Card } from "primereact/card";

const permissionList = ["No Access Permission", "Read only", "Read and Write"];

export default function UserAccessPermission() {
  const operatorId = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")).operator_id
    : "";

  // constants
  const [permissions, setPermissions] = React.useState([
    {
      id: 0,
      text: "Set all to no access",
      checked: true,
    },
    {
      id: 1,
      text: "Set all to read only",
      checked: false,
    },
    {
      id: 2,
      text: " Set all to read and write",
      checked: false,
    },
    {
      id: 3,
      text: "Custom Selection",
      checked: true,
    },
  ]);

  const navigate = useNavigate();
  const { operatorAccountId } = useParams();
  console.log({ operatorAccountId });

  const [pageGroup, setPageGroup] = React.useState([
    { value: "manage-operator-account", checked: "3" },
    { value: "lockers", checked: "3" },
    { value: "locker-users", checked: "3" },
    { value: "agents", checked: "3" },
    { value: "partners", checked: "3" },
    { value: "settings", checked: "3" },
    { value: "campaigns", checked: "3" },
    { value: "reports", checked: "3" },
    { value: "operations-report", checked: "3" },
  ]);

  const [pageList, setPageList] = React.useState(pageListArray);
  const [pageListOriginal, setPageListOriginal] = React.useState(pageListArray);

  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [pageAccessList, setPageAccessList] = React.useState(noPermissionRole);

  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );

  const pendingNavigation = useSidebarStore((state) => state.pendingNavigation);
  const setPendingNavigation = useSidebarStore(
    (state) => state.setPendingNavigation
  );
  const activeSubItem = useSidebarStore((state) => state.activeSubItem);
  const setActiveSubItem = useSidebarStore((state) => state.setActiveSubItem);
  const localAccessedSidebar = useSidebarStore(
    (state) => state.localAccessedSidebar
  );

  // GraphQL queries and mutations
  const allOperatorAccounts = useQuery(ALL_OPERATOR_ACCOUNTS, {
    fetchPolicy: "no-cache",
  });

  const [updateUserAccessPermission] = useMutation(
    UPDATE_USER_ACCESS_PERMISSION
  );

  const [operatorRoleName, { data: roleNameII }] = useLazyQuery(
    OPERATOR_ACCOUNT_ROLE_BY_ID
  );

  const operatorAccountDetails = useQuery(GET_ACCOUNT_ACCESS_PERMISSIONS, {
    variables: {
      operatorId: operatorId,
      operatorAccountId: operatorAccountId,
    },
  });

  console.log({ operatorAccountDetails, roleNameII });

  const accountData = useMemo(() => {
    // Return early if data is not available
    if (!operatorAccountDetails?.data?.findOperatorAccountById?.length) {
      return null;
    }

    const account = operatorAccountDetails.data.findOperatorAccountById[0];

    // Parse the accessPermissions JSON string
    let parsedPermissions = {};
    try {
      parsedPermissions = JSON.parse(account.accessPermissions);
    } catch (error) {
      console.error("Error parsing access permissions:", error);
      parsedPermissions = {};
    }

    // Return processed account data
    return {
      username: account.username,
      operator: {
        operatorId: account.operator.operatorId,
        company: account.operator.company,
      },
      active: account.active,
      adminAccess: account.adminAccess,
      accessPermissions: parsedPermissions,
      userEmail: account.userEmail,
      operatorAccountRoleId: account.operatorAccountRoleId,
      roleName: roleNameII?.operatorAccountRoleById?.roleName,
    };
  }, [operatorAccountDetails?.data, roleNameII]);

  useEffect(() => {
    if (accountData?.operatorAccountRoleId) {
      operatorRoleName({
        variables: {
          operatorAccountRoleId: accountData.operatorAccountRoleId,
        },
      });
    }
  }, [accountData?.operatorAccountRoleId]);

  console.log({ accountData });

  // Updated transformSidebarToPageList function to handle nested items
  const transformSidebarToPageList = (sidebar) => {
    return sidebar.flatMap((section) =>
      section.content.flatMap((item) => {
        // Base item
        const baseItem = {
          name: item.title,
          group: section.title.toLowerCase().replaceAll(" ", "-"),
          value: Array.isArray(item.tag) ? item.tag[0] : item.tag, // Use first tag if array
          permissions: [
            { id: "0", label: "No Access", checked: false },
            { id: "1", label: "Read Only", checked: false },
            { id: "2", label: "Read & Write", checked: false },
          ],
        };

        // Check if item has nested children (type: "accordion" and content array)
        if (
          item.type === "accordion" &&
          item.content &&
          item.content.length > 0
        ) {
          // Return both parent and children
          return [
            baseItem,
            ...item.content.map((child) => ({
              name: `${item.title} - ${child.title}`, // Prefix with parent name
              group: section.title.toLowerCase().replaceAll(" ", "-"),
              value: Array.isArray(child.tag) ? child.tag[0] : child.tag,
              parentValue: Array.isArray(item.tag) ? item.tag[0] : item.tag, // Reference to parent
              isChild: true, // Mark as child item
              permissions: [
                { id: "0", label: "No Access", checked: false },
                { id: "1", label: "Read Only", checked: false },
                { id: "2", label: "Read & Write", checked: false },
              ],
            })),
          ];
        }

        // Return just the base item if no children
        return [baseItem];
      })
    );
  };

  const breadcrumbItems = [
    { label: "Operator Account", path: `/operator-accounts` },
    { label: "Account Profile", path: "#" },
  ];

  useEffect(() => {
    if (localAccessedSidebar?.length > 0) {
      const updatedPageList = transformSidebarToPageList(localAccessedSidebar);
      setPageList(updatedPageList);

      // Initialize pageGroup based on accessible pages
      const accessibleGroups = updatedPageList.reduce((acc, page) => {
        const groupExists = acc.find((group) => group.value === page.group);
        if (!groupExists) {
          const defaultPermission = page.permissions.find(
            (perm) => perm.checked
          ) || { id: "0" }; // Default to No Access
          acc.push({ value: page.group, checked: defaultPermission.id });
        }
        return acc;
      }, []);

      setPageGroup(accessibleGroups);
    }
  }, [localAccessedSidebar]);

  const validateFields = () => {
    const invalidGroups = pageGroup.filter((group) => {
      if (group.checked === "3") {
        // Find corresponding pages in pageList
        const relatedPages = pageList.filter(
          (page) => page.group === group.value
        );

        const hasInvalidPermissions = relatedPages.some((page) =>
          page.permissions.every((permission) => !permission.checked)
        );

        return hasInvalidPermissions;
      }
      return false;
    });

    if (invalidGroups.length > 0) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Error",
        message:
          "Some groups are marked as 'Custom assign' but have no permissions checked.",
        open: true,
        actions: (
          <div className="gap-2 w-full mx-auto">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"OK"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
      return;
    } else {
      handleSaveChanges();
    }
  };

  console.log({ pageList });
  const handleSaveChanges = () => {
    let tempObj = {};

    pageList.forEach((data, index) => {
      // Check if this is a parent with children
      const hasChildren = pageList.some(
        (child) => child.parentValue === data.value && child.isChild
      );

      // Only process permissions for:
      // 1. Child items (always have radio buttons)
      // 2. Parent items without children (have radio buttons)
      if (data.isChild || !hasChildren) {
        data.permissions.forEach((item, i) => {
          if (item.checked === true) {
            tempObj[data.value] = item.id;
          }
        });
      }
      // Skip parent items that have children (no radio buttons)
    });

    const parameter = {
      operatorAccountId: operatorAccountId,
      accessPermissions: JSON.stringify(tempObj),
    };

    updateUserAccessPermission({
      variables: { userAccessPermissionArgs: parameter },
      fetchPolicy: "no-cache",
    })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        allOperatorAccounts.refetch();
      });

    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
      title: Strings.popup_success_message_edit,
      message: Strings.popup_success_message_edit,
      open: true,
      actions: (
        <div className="gap-2 w-full mx-auto">
          <Button
            onClick={() => {
              setValidation({ open: false });
              window.location.reload();
            }}
            value={"OK"}
            className={"p-2 w-full"}
          />
        </div>
      ),
    });
  };

  // Main effect to handle accountData changes (replaces the operatorData effect)
  React.useEffect(() => {
    if (accountData) {
      console.log({ accountData });

      if (
        accountData.accessPermissions &&
        Object.keys(accountData.accessPermissions).length > 0
      ) {
        setPageAccessList(accountData.accessPermissions);
        const parsedPermissions = accountData.accessPermissions;
        console.log({ parsedPermissions });

        let tempPageList = [...pageList];

        tempPageList.forEach((page, pageIndex) => {
          Object.entries(parsedPermissions).forEach(
            ([pageValue, permissionId]) => {
              if (page.value === pageValue) {
                page.permissions.forEach((permission, permissionIndex) => {
                  // Compare id and set the checked value accordingly
                  tempPageList[pageIndex].permissions[permissionIndex].checked =
                    JSON.stringify(permission.id) ===
                    JSON.stringify(permissionId);
                });
              }
            }
          );
        });

        // Set pageGroup based on permissions
        const updatedPageGroup = pageGroup.map((group) => {
          const groupPages = tempPageList.filter(
            (page) => page.group === group.value
          );

          if (groupPages.length > 0) {
            const allNoAccess = groupPages.every((page) =>
              page.permissions.some(
                (permission) => permission.checked && permission.id === "0"
              )
            );
            const allReadOnly = groupPages.every((page) =>
              page.permissions.some(
                (permission) => permission.checked && permission.id === "1"
              )
            );
            const allReadWrite = groupPages.every((page) =>
              page.permissions.some(
                (permission) => permission.checked && permission.id === "2"
              )
            );

            let checkedValue = "3";
            if (allNoAccess) {
              checkedValue = "0";
            } else if (allReadOnly) {
              checkedValue = "1";
            } else if (allReadWrite) {
              checkedValue = "2";
            }

            return { ...group, checked: checkedValue };
          } else {
            return group;
          }
        });

        setPageList(tempPageList);
        setPageGroup(updatedPageGroup);
      } else {
        // Set default permissions when no existing permissions
        setPageList([
          {
            value: "lockers",
            name: "Lockers",
            group: "lockers",
            permissions: [
              { id: "0", text: "No Access", checked: true },
              { id: "1", text: "Read Only", checked: false },
              { id: "2", text: "Read and Write", checked: false },
            ],
          },
          {
            value: "lockerFees",
            name: "Locker Fees",
            group: "lockers",
            permissions: [
              { id: "0", text: "No Access", checked: true },
              { id: "1", text: "Read Only", checked: false },
              { id: "2", text: "Read and Write", checked: false },
            ],
          },
          // ... (rest of the default permissions structure remains the same)
          {
            value: "lockerClearingSummary",
            name: "Locker Clearing Summary",
            group: "operations-report",
            permissions: [
              { id: "0", text: "No Access", checked: true },
              { id: "1", text: "Read Only", checked: false },
              { id: "2", text: "Read and Write", checked: false },
            ],
          },
        ]);

        // Set default pageAccessList
        setPageAccessList({
          lockers: "0",
          lockerFees: "0",
          lockerStorageFees: "0",
          // ... (rest of default values)
          lockerClearingSummary: "0",
        });

        setPageGroup([
          { value: "lockers", checked: "3" },
          { value: "locker-users", checked: "3" },
          { value: "agents", checked: "3" },
          { value: "partners", checked: "3" },
          { value: "settings", checked: "3" },
          { value: "campaigns", checked: "3" },
          { value: "reports", checked: "3" },
          { value: "operations-report", checked: "3" },
        ]);
      }
    }
  }, [accountData]);
  console.log({ localAccessedSidebar });

  const handleOnGroupCheckChange = (event) => {
    const { name, value, id } = event.target;
    setChangesMade(true);
    let tempObj = pageGroup.find((x) => x.value === name.split("hello")[0]);
    let index = pageGroup.findIndex((x) => x.value === name.split("hello")[0]);
    let tempPageGroup = [...pageGroup];
    tempObj.checked = id;
    tempPageGroup[index] = tempObj;
    console.log(tempPageGroup);
    setPageGroup(tempPageGroup);

    // set something in the main pages
    let tempPageList = [...pageList];
    pageList.forEach((element, index) => {
      if (element.group === name.split("hello")[0]) {
        pageList[index].permissions.forEach((element2, index2) => {
          if (tempPageList[index].permissions[index2].id === id) {
            tempPageList[index].permissions[index2].checked = true;
          } else {
            tempPageList[index].permissions[index2].checked = false;
          }
        });
      }
    });
    setPageList(tempPageList);
  };

  useEffect(() => {
    if (pendingNavigation !== null) {
      if (changesMade) {
        console.log("open dialog");
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: `${Strings.popup_confirm_cancel_title}?`,
          message: Strings.popup_confirm_cancel_massage,
          open: true,
          actions: (
            <div className="flex justify-between gap-3">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                  setPendingNavigation(null);
                  setActiveSubItem(activeSubItem);
                }}
                value={"No"}
                className={"p-2 w-full"}
              />
              <Button
                onClick={() => {
                  navigate(pendingNavigation);
                  setPendingNavigation(null);
                }}
                value={"Yes"}
                className={"p-2 w-full"}
              />
            </div>
          ),
        });
      } else {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    }
  }, [pendingNavigation, changesMade]);

  console.log({ changesMade });

  return (
    <>
      {" "}
      <Breadcrumbs items={breadcrumbItems} />
      <div className="mt-2"></div>
      <ContentContainer>
        <>
          <div className="text-lg font-bold mb-4 uppercase">
            {accountData?.username}
          </div>
          <div className="grid grid-cols-10 items-center space-y-2">
            <div className="col-span-2">Username:</div>
            <div className="col-span-3">
              <Textfield disabled={true} value={accountData?.username} />
            </div>
            <div className="col-span-2 pl-3">Operator:</div>
            <div className="col-span-3">
              <Textfield
                disabled={true}
                value={accountData?.operator?.company}
              />
            </div>

            <div className="col-span-2">Email:</div>
            <div className="col-span-3">
              <Textfield disabled={true} value={accountData?.userEmail} />
            </div>

            <div className="col-span-5"></div>
            <div className="col-span-2">Roles/Permission:</div>
            <div className="col-span-3">
              <Textfield disabled={true} value={accountData?.roleName} />
            </div>
          </div>

          <div className="grid grid-cols-10 items-center space-y-2 pt-4">
            <div className="col-span-10 font-semibold">
              Edit specific account's access permission
            </div>

            <Card className="col-span-10">
              <>
                {accountData && (
                  <>
                    {/* <div className="border-t border-s border-e border-secondary-darkGray bg-primary-primaryYellow p-6 w-full mt-4">
                    <p className="text-center font-bold text-xl">
                      {accountData.username}
                    </p>
                  </div> */}

                    <table className="w-full border">
                      <tbody>
                        {pageGroup.map((group, key) => {
                          return (
                            <tr className="w-full" key={key}>
                              <td
                                className="border border-secondary-darkGray w-full"
                                colSpan={2}
                              >
                                <Accordion style={{ width: "100%" }}>
                                  <AccordionTab
                                    headerClassName="w-full"
                                    header={group.value
                                      .toUpperCase()
                                      .replaceAll("-", " ")}
                                    style={{ width: "100%" }}
                                  >
                                    <label className=" items-start space-x-2 p-1 flex mt-4">
                                      <input
                                        type="radio"
                                        name={group.value + "hello"}
                                        className="form-radio h-5 w-5 text-blue-600 my-auto"
                                        checked={group.checked === "0"}
                                        id={"0"}
                                        disabled={
                                          accessPermission.rolesAndTemplates ===
                                            1 ||
                                          accessPermission.rolesAndTemplates ===
                                            "1"
                                        }
                                        onChange={handleOnGroupCheckChange}
                                      />
                                      <span className="my-auto">
                                        Set all to no access
                                      </span>
                                    </label>
                                    <label className=" items-start space-x-2 p-1 flex">
                                      <input
                                        type="radio"
                                        name={group.value + "hello"}
                                        className="form-radio h-5 w-5 text-blue-600 my-auto"
                                        checked={group.checked === "1"}
                                        id={"1"}
                                        disabled={
                                          accessPermission.rolesAndTemplates ===
                                            1 ||
                                          accessPermission.rolesAndTemplates ===
                                            "1"
                                        }
                                        onChange={handleOnGroupCheckChange}
                                      />
                                      <span className="my-auto">
                                        Set all to read only
                                      </span>
                                    </label>
                                    <label className=" items-start space-x-2 p-1 flex">
                                      <input
                                        type="radio"
                                        name={group.value + "hello"}
                                        className="form-radio h-5 w-5 text-blue-600 my-auto"
                                        checked={group.checked === "2"}
                                        id={"2"}
                                        disabled={
                                          accessPermission.rolesAndTemplates ===
                                            1 ||
                                          accessPermission.rolesAndTemplates ===
                                            "1"
                                        }
                                        onChange={handleOnGroupCheckChange}
                                      />
                                      <span className="my-auto">
                                        Set all to read and write
                                      </span>
                                    </label>
                                    <label className=" items-start space-x-2 p-1 flex mb-4">
                                      <input
                                        type="radio"
                                        name={group.value + "hello"}
                                        className="form-radio h-5 w-5 text-blue-600 my-auto"
                                        checked={group.checked === "3"}
                                        id={"3"}
                                        disabled={
                                          accessPermission.rolesAndTemplates ===
                                            1 ||
                                          accessPermission.rolesAndTemplates ===
                                            "1"
                                        }
                                        onChange={handleOnGroupCheckChange}
                                      />
                                      <span className="my-auto">
                                        Custom assign
                                      </span>
                                    </label>
                                    {pageList.map((data, key) => {
                                      if (data.group === group.value) {
                                        // Only render parent items here
                                        if (!data.isChild) {
                                          const children = pageList.filter(
                                            (child) =>
                                              child.parentValue ===
                                                data.value && child.isChild
                                          );

                                          return (
                                            <React.Fragment key={key}>
                                              {/* Parent row - Show radio buttons only if no children */}
                                              <tr className="w-full">
                                                <td className="p-2 border border-secondary-darkGray w-2/4 font-medium">
                                                  <p>{data.name}</p>
                                                </td>
                                                <td className="p-2 border border-secondary-darkGray">
                                                  {children.length > 0 ? (
                                                    // No radio buttons if has children
                                                    <div></div>
                                                  ) : (
                                                    // Show radio buttons if no children
                                                    <div>
                                                      <RadioButtonList
                                                        disabled={
                                                          pageGroup.find(
                                                            (x) =>
                                                              x.value ===
                                                              data.group
                                                          ).checked !== "3" ||
                                                          accessPermission.rolesAndTemplates ===
                                                            1 ||
                                                          accessPermission.rolesAndTemplates ===
                                                            "1"
                                                        }
                                                        dataItems={
                                                          data.permissions
                                                        }
                                                        name={data.value}
                                                        handleChange={(e) => {
                                                          let tempVar = [
                                                            ...data.permissions,
                                                          ];
                                                          data.permissions.forEach(
                                                            (
                                                              element,
                                                              index
                                                            ) => {
                                                              if (
                                                                element.id === e
                                                              ) {
                                                                tempVar[
                                                                  index
                                                                ].checked = true;
                                                              } else {
                                                                tempVar[
                                                                  index
                                                                ].checked = false;
                                                              }
                                                            }
                                                          );

                                                          let tempArr = [
                                                            ...pageList,
                                                          ];
                                                          const parentIndex =
                                                            pageList.findIndex(
                                                              (p) =>
                                                                p.value ===
                                                                data.value
                                                            );
                                                          tempArr[
                                                            parentIndex
                                                          ].permissions =
                                                            tempVar;
                                                          setPageList(tempArr);
                                                          setChangesMade(true);
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                </td>
                                              </tr>

                                              {/* Children rows - WITH RADIO BUTTONS */}
                                              {children.map(
                                                (child, childIndex) => {
                                                  const childKey =
                                                    pageList.findIndex(
                                                      (p) =>
                                                        p.value === child.value
                                                    );
                                                  return (
                                                    <tr
                                                      className="w-full"
                                                      key={`${key}-child-${childIndex}`}
                                                    >
                                                      <td className="p-2 border border-secondary-darkGray w-2/4 pl-8 bg-gray-50">
                                                        <p className="text-sm text-gray-700">
                                                          ↳{" "}
                                                          {child.name.replace(
                                                            `${data.name} - `,
                                                            ""
                                                          )}
                                                        </p>
                                                      </td>
                                                      <td className="p-2 border border-secondary-darkGray bg-gray-50">
                                                        <div>
                                                          <RadioButtonList
                                                            disabled={
                                                              pageGroup.find(
                                                                (x) =>
                                                                  x.value ===
                                                                  child.group
                                                              ).checked !==
                                                                "3" ||
                                                              accessPermission.rolesAndTemplates ===
                                                                1 ||
                                                              accessPermission.rolesAndTemplates ===
                                                                "1"
                                                            }
                                                            dataItems={
                                                              child.permissions
                                                            }
                                                            name={child.value}
                                                            handleChange={(
                                                              e
                                                            ) => {
                                                              let tempVar = [
                                                                ...child.permissions,
                                                              ];
                                                              child.permissions.forEach(
                                                                (
                                                                  element,
                                                                  index
                                                                ) => {
                                                                  if (
                                                                    element.id ===
                                                                    e
                                                                  ) {
                                                                    tempVar[
                                                                      index
                                                                    ].checked = true;
                                                                  } else {
                                                                    tempVar[
                                                                      index
                                                                    ].checked = false;
                                                                  }
                                                                }
                                                              );

                                                              let tempArr = [
                                                                ...pageList,
                                                              ];
                                                              tempArr[
                                                                childKey
                                                              ].permissions =
                                                                tempVar;
                                                              setPageList(
                                                                tempArr
                                                              );
                                                              setChangesMade(
                                                                true
                                                              );
                                                            }}
                                                          />
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  );
                                                }
                                              )}
                                            </React.Fragment>
                                          );
                                        }
                                        return null; // Don't render child items separately
                                      }
                                    })}
                                  </AccordionTab>
                                </Accordion>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {changesMade && (
                      <div className="grid grid-cols-2 gap-2 w-full pt-4 mx-auto">
                        <Button
                          value={"Cancel"}
                          className={"p-2"}
                          onClick={() => {
                            setValidation({
                              icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                              title: Strings.popup_confirm_cancel_title,
                              message: Strings.popup_confirm_cancel_massage,
                              open: true,
                              actions: (
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    onClick={() =>
                                      setValidation({ open: false })
                                    }
                                    value={"No"}
                                    className={"p-2"}
                                  />
                                  <Button
                                    onClick={() => {
                                      window.location.reload();
                                    }}
                                    value={"Yes"}
                                    className={"p-2"}
                                  />
                                </div>
                              ),
                            });
                          }}
                        />
                        <Button
                          value={"Save Changes"}
                          className={"p-2"}
                          onClick={() => {
                            setValidation({
                              icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                              title: Strings.popup_confirm_save_title,
                              message: Strings.popup_confirm_save_massage,
                              open: true,
                              actions: (
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    onClick={() =>
                                      setValidation({ open: false })
                                    }
                                    value={"No"}
                                    className={"p-2"}
                                  />
                                  <Button
                                    onClick={validateFields}
                                    value={"Yes"}
                                    className={"p-2"}
                                  />
                                </div>
                              ),
                            });
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            </Card>
          </div>
        </>
        <Modal
          icon={validation.icon}
          title={validation.title}
          message={validation.message}
          open={validation.open}
          actions={validation.actions}
        />
      </ContentContainer>
    </>
  );
}
